import { ButtonHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

import { LoadingDots } from '../LoadingDots';

type ButtonVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'destructive';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  submitting?: boolean;
  variant?: ButtonVariant;
}

const getVariantClasses = (variant: ButtonVariant = 'default') => {
  const baseClasses = [
    'border',
    'font-bold',
    'h-10',
    'px-3.5',
    'rounded-xl',
    'text-sm',
    'disabled:cursor-not-allowed',
    'disabled:opacity-50',
    'transition-colors',
    'flex',
    'items-center',
    'justify-center',
    'gap-2',
  ];

  switch (variant) {
    case 'secondary':
      return [
        ...baseClasses,
        'bg-neutral-100',
        'border-neutral-200',
        'text-neutral-900',
        'hover:bg-neutral-200',
        'focus:bg-neutral-200',
        'focus:border-neutral-300',
        'active:bg-neutral-300',
      ];
    case 'outline':
      return [
        ...baseClasses,
        'bg-transparent',
        'border-neutral-300',
        'text-neutral-700',
        'hover:bg-neutral-50',
        'focus:bg-neutral-50',
        'focus:border-neutral-400',
        'active:bg-neutral-100',
      ];
    case 'ghost':
      return [
        ...baseClasses,
        'bg-transparent',
        'border-transparent',
        'text-neutral-700',
        'hover:bg-neutral-100',
        'focus:bg-neutral-100',
        'active:bg-neutral-200',
      ];
    case 'destructive':
      return [
        ...baseClasses,
        'bg-red-600',
        'border-transparent',
        'text-white',
        'hover:bg-red-700',
        'focus:bg-red-700',
        'focus:border-red-300',
        'active:bg-red-800',
      ];
    default:
      return [
        ...baseClasses,
        'bg-linear-[180deg,#2EA597_-4.4%,#2B8D8F_41.09%,#2B888F_100%]',
        'border-transparent',
        'text-white',
        'hover:bg-linear-[180deg,#278C80_-4.4%,#237475_41.09%,#237075_100%]',
        'focus:bg-linear-[180deg,#278C80_-4.4%,#237475_41.09%,#237075_100%]',
        'focus:border-neutral-300',
        'active:bg-linear-[180deg,#278C80_-4.4%,#1F6566_41.09%,#1F6166_100%]',
        'disabled:hover:bg-linear-[180deg,#2EA597_-4.4%,#2B8D8F_41.09%,#2B888F_100%]',
      ];
  }
};

export function Button(props: Props) {
  const {
    className,
    children,
    submitting,
    variant = 'default',
    type = 'button',
    ...rest
  } = props;

  return (
    <button
      type={type}
      className={twMerge(...getVariantClasses(variant), className)}
      {...rest}
    >
      {submitting ? <LoadingDots /> : children}
    </button>
  );
}
